import React, { useMemo, useState } from 'react';
import { GlobalInput } from './GlobalInput';
import styles from './GlobalEntityAutocomplete.module.css';

export interface EntityOption {
  id: string;
  label: string;
  title?: string;
}

interface GlobalEntityAutocompleteProps {
  label: string;
  placeholder?: string;
  selectedIds: string[];
  options: EntityOption[];
  onChange: (ids: string[]) => void;
  readOnlyIds?: string[]; // IDs that are linked from the reverse direction (read-only)
}

export const GlobalEntityAutocomplete: React.FC<GlobalEntityAutocompleteProps> = ({
  label,
  placeholder,
  selectedIds,
  options,
  onChange,
  readOnlyIds = []
}) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(q) || opt.id.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (id: string) => {
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setQuery('');
  };

  const handleRemove = (id: string) => {
    // Don't allow removing read-only IDs (reverse links)
    if (readOnlyIds.includes(id)) return;
    onChange(selectedIds.filter(x => x !== id));
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputRow}>
        <GlobalInput
          label={label}
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
        />
        {filtered.length > 0 && query.trim() && (
          <div className={styles.options}>
            {filtered.map(opt => (
              <button
                type="button"
                key={opt.id}
                className={styles.option}
                onClick={() => handleSelect(opt.id)}
              >
                <span className={styles.optionId}>{opt.id}</span>
                <span className={styles.optionLabel}>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {(selectedIds.length > 0 || readOnlyIds.length > 0) && (
        <div className={styles.chips}>
          {/* Display forward links (editable) */}
          {selectedIds.map(id => {
            const opt = options.find(o => o.id === id);
            const isReadOnly = readOnlyIds.includes(id);
            return (
              <span key={id} className={`${styles.chip} ${isReadOnly ? styles.chipReadOnly : ''}`}>
                <span className={styles.chipId}>{opt ? opt.id : id}</span>
                {opt && (opt.title || opt.label) && (
                  <span className={styles.chipLabel}>{opt.title || opt.label}</span>
                )}
                {!isReadOnly && (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => handleRemove(id)}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
          {/* Display reverse links (read-only) that aren't already in selectedIds */}
          {readOnlyIds.filter(id => !selectedIds.includes(id)).map(id => {
            const opt = options.find(o => o.id === id);
            return (
              <span key={id} className={`${styles.chip} ${styles.chipReadOnly}`}>
                <span className={styles.chipId}>{opt ? opt.id : id}</span>
                {opt && (opt.title || opt.label) && (
                  <span className={styles.chipLabel}>{opt.title || opt.label}</span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};


